/**
 * Service Worker Update Manager for FlowRide PWA
 * Provides intelligent update strategies for better user experience
 */


// État global pour gérer les mises à jour
let updateAvailable = false;
let registration: ServiceWorkerRegistration | null = null;

/**
 * Initialize service worker update management
 */
export async function initializeServiceWorker(): Promise<void> {
    if ( !( "serviceWorker" in navigator ) ) {
        return;
    }

    try {
        const reg = await navigator.serviceWorker.getRegistration();
        if ( !reg ) {
            return;
        }
        registration = reg;

        // Écouter les mises à jour disponibles
        registration.addEventListener( "updatefound", handleUpdateFound );

        // Vérifier les mises à jour périodiquement (toutes les 30 minutes)
        setInterval( () => {
            if ( registration ) {
                registration.update().catch( console.warn );
            }
        }, 30 * 60 * 1000 );

        // Vérifier immédiatement
        await registration.update();
    } catch( error ) {
        console.warn( "Service Worker initialization failed:", error );
    }
}

/**
 * Handle when a new service worker is found
 */
function handleUpdateFound(): void {
    if ( !registration ) {
        return;
    }

    const installing = registration.installing;
    if ( !installing ) {
        return;
    }

    installing.addEventListener( "statechange", () => {
        if ( installing.state === "installed" && registration?.waiting ) {
            updateAvailable = true;
            showUpdateNotification();
        }
    } );
}

/**
 * Show update notification to user
 */
function showUpdateNotification(): void {
    /*
     * Désactivé - popup de mise à jour inutile
     * Les mises à jour se font automatiquement en arrière-plan
     */
    return;
}


/**
 * Show in-app update notification (fallback)
 * Currently unused but kept for future implementation
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function showInAppUpdateNotification(): void {
    // Créer un élément de notification dans l'UI
    const notification = document.createElement( "div" );
    notification.className = "update-notification";
    notification.innerHTML = `
    <div class="update-content">
      <h3>🔄 Mise à jour disponible</h3>
      <p>Une nouvelle version de FlowRide est disponible.</p>
      <div class="update-actions">
        <button class="update-btn" onclick="window.applyUpdate()">Mettre à jour</button>
        <button class="dismiss-btn" onclick="this.parentElement.parentElement.parentElement.remove()">Plus tard</button>
      </div>
    </div>
  `;

    // Styles inline pour la notification
    notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 10000;
    max-width: 300px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  `;

    const content = notification.querySelector( ".update-content" ) as HTMLElement;
    content.style.cssText = `
    padding: 16px;
  `;

    const actions = notification.querySelector( ".update-actions" ) as HTMLElement;
    actions.style.cssText = `
    display: flex;
    gap: 8px;
    margin-top: 12px;
  `;

    const updateBtn = notification.querySelector( ".update-btn" ) as HTMLButtonElement;
    updateBtn.style.cssText = `
    background: #007bff;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
  `;

    const dismissBtn = notification.querySelector( ".dismiss-btn" ) as HTMLButtonElement;
    dismissBtn.style.cssText = `
    background: #6c757d;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
  `;

    document.body.appendChild( notification );

    // Auto-supprimer après 30 secondes
    setTimeout( () => {
        if ( notification.parentElement ) {
            notification.remove();
        }
    }, 30000 );
}

/**
 * Apply the service worker update
 */
export function applyUpdate(): void {
    if ( registration?.waiting === undefined || registration?.waiting === null ) {
        return;
    }

    // Envoyer le message pour activer le nouveau service worker
    registration.waiting.postMessage( { "type": "SKIP_WAITING" } );

    // Attendre que le nouveau service worker prenne le contrôle
    registration.waiting.addEventListener( "statechange", ( e ) => {
        const sw = e.target as ServiceWorker;
        if ( sw.state === "activated" ) {
            // Recharger la page pour utiliser la nouvelle version
            window.location.reload();
        }
    } );
}

/**
 * Check if an update is available
 */
export function isUpdateAvailable(): boolean {
    return updateAvailable;
}

/**
 * Force check for updates
 */
export async function checkForUpdates(): Promise<boolean> {
    if ( !registration ) {
        return false;
    }

    try {
        await registration.update();

        return updateAvailable;
    } catch( error ) {
        console.warn( "Failed to check for updates:", error );

        return false;
    }
}

// Exposer la fonction globalement pour les boutons
declare global {
    interface Window { "applyUpdate"?: () => void }
}
window.applyUpdate = applyUpdate;
